// v-comma : 숫자 입력란 천단위 콤마 "표시 전용" 디렉티브
//
// 핵심 계약 — 모델값 불변:
//  - input.value(getter) 는 항상 콤마 없는 원값(raw)을 돌려준다 → Vue v-model / Vuetify 내부 / 기존 코드가 원값을 그대로 읽는다.
//  - 포커스 중에는 원값 그대로 표시(직접 수정), 포커스가 없으면 콤마 형식으로 표시.
//  - 포커스 IN/OUT 시 표시만 바꾸며 input/change 이벤트를 발생시키지 않는다.
//
// 사용: <v-text-field v-comma ...>, <v-number-input v-comma ...>, <input v-comma ...>
//  - .int      : inputmode="numeric" + '.' 입력 차단(정수 전용 필드 — 수량/입출금액). 기본은 inputmode="decimal".
//  - .unsigned : '-' 입력 금지 (min 이 0 이상인 필드).
//  - 인자      : v-comma:numeric / v-comma:decimal 로 inputmode 직접 지정 가능.
//
// 원래 type="number" 였던 input 은 type="text" 로 바꾸고, type=number 의 동작을 재현한다:
//  - 입력 제한: 숫자 · '.'(1개) · '-'(선두만) 외 문자 제거(콤마 포함 붙여넣기 → 콤마 제거).
//  - value 정화: 프로그램이 NaN/Infinity 등 숫자가 아닌 값을 넣으면 '' (native number 와 동일),
//    입력 중간 상태 '-', '.', '-.' 는 value '' 로 읽힘(native number 와 동일).
//  - ↑/↓ 키 증감(step/min/max) — 단, v-number-input 은 Vuetify 가 자체 처리하므로 제외.

// ───────────── 순수 함수 (단위 테스트 대상) ─────────────

/** 천단위 콤마 형식. 소수부는 그대로, 음수 부호 유지, 정수부 선행 0 제거('007' → '7'). 숫자 형식이 아니면 그대로 반환. */
export function formatComma(value: unknown): string {
	if (value === null || value === undefined) return '';
	const s = String(value);
	const m = /^(-?)(\d+)(\.\d*)?$/.exec(s);
	if (!m) return s; // '', '-', '.', '.5', '1e+21', 'NaN', 비숫자 등은 그대로
	const int = m[2].replace(/^0+(?=\d)/, '');
	return m[1] + int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (m[3] ?? '');
}

/** 콤마 제거 */
export function stripComma(value: unknown): string {
	if (value === null || value === undefined) return '';
	return String(value).replace(/,/g, '');
}

/**
 * 입력 문자 정제: 숫자 · '.'(첫 1개만, allowDot 일 때) · '-'(선두만, allowNegative 일 때) 외 문자 제거.
 * caret 이 주어지면 정제 후 caret 위치를 함께 돌려준다(남은 문자 기준으로 보존).
 */
export function sanitizeNumberText(text: string, allowNegative = true, caret?: number | null, allowDot = true): { value: string; caret: number } {
	const src = text ?? '';
	let out = '';
	let hasDot = false;
	let newCaret = 0;
	const c = caret ?? src.length;
	for (let i = 0; i < src.length; i++) {
		const ch = src[i];
		let keep = false;
		if (ch >= '0' && ch <= '9') keep = true;
		else if (ch === '.' && allowDot && !hasDot) { keep = true; hasDot = true; }
		else if (ch === '-' && allowNegative && out.length === 0) keep = true;
		if (keep) {
			out += ch;
			if (i < c) newCaret++;
		}
	}
	return { value: out, caret: newCaret };
}

/** type=number 의 value 정화 재현: 유효 숫자 문자열이 아니면(NaN/Infinity/비숫자) '' */
export function sanitizeNumberValue(value: unknown): string {
	if (value === null || value === undefined) return '';
	const s = String(value);
	if (s === '') return '';
	if (/^-?(\d+(\.\d+)?|\.\d+)([eE][-+]?\d+)?$/.test(s) && isFinite(Number(s))) return s;
	return '';
}

/**
 * type=number 의 사용자 입력 읽기 재현(Chrome 실측):
 *  '-', '.', '-.' → '' / 끝이 '.' 인 '1.' → '1' / 그 외 그대로.
 */
export function readNumberValue(raw: string): string {
	if (/^-?\.?$/.test(raw)) return '';
	return raw.endsWith('.') ? raw.slice(0, -1) : raw;
}

// ───────────── 디렉티브 ─────────────

interface CommaState {
	input: HTMLInputElement;
	raw: string;
	focused: boolean;
	numberMode: boolean;   // 원래 type="number"
	allowNegative: boolean;
	allowDot: boolean;     // .int 가 아니면 '.' 허용
	get: () => string;     // native getter
	set: (v: string) => void; // native setter
	listeners: Array<[string, EventListener, boolean]>;
}

const STATE_KEY = '__vComma';

function findInput(el: HTMLElement): HTMLInputElement | null {
	if (el instanceof HTMLInputElement) return el;
	return el?.querySelector ? el.querySelector('input') : null;
}

function decimalsOf(n: number): number {
	const s = String(n);
	const i = s.indexOf('.');
	return i < 0 ? 0 : s.length - i - 1;
}

function bind(el: HTMLElement, binding: any) {
	const input = findInput(el);
	if (!input || (input as any)[STATE_KEY]) return;

	const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
	if (!desc || !desc.get || !desc.set) return;
	const get = () => desc.get!.call(input) as string;
	const set = (v: string) => desc.set!.call(input, v);

	const mods = binding?.modifiers ?? {};
	const numberMode = (input.getAttribute('type') || '').toLowerCase() === 'number';
	if (numberMode) input.setAttribute('type', 'text');
	const inputmode = binding?.arg === 'numeric' || binding?.arg === 'decimal' ? binding.arg : (mods.int ? 'numeric' : 'decimal');
	input.setAttribute('inputmode', inputmode);
	input.setAttribute('autocomplete', 'off');

	const st: CommaState = {
		input,
		raw: '',
		focused: document.activeElement === input,
		numberMode,
		allowNegative: !mods.unsigned,
		allowDot: !mods.int,
		get,
		set,
		listeners: [],
	};

	const normalize = (v: unknown): string => {
		const s = stripComma(v);
		return st.numberMode ? sanitizeNumberValue(s) : s;
	};
	const toValue = (raw: string) => (st.numberMode ? readNumberValue(raw) : raw);
	// 표시 문자열 → 원값: 콤마 제거 + 입력 제한 정제. 리스너 실행 순서·IME 조합 상태와 무관하게
	//  모델(getter)에는 항상 숫자 문자열만 들어가도록 보장한다(조합 중 '1ㄱ' 이 규칙/계산에 흘러들어가는 것 방지).
	const cleanText = (text: string) => sanitizeNumberText(stripComma(text), st.allowNegative, null, st.allowDot).value;
	const render = () => {
		const shown = st.focused ? st.raw : formatComma(st.raw);
		if (get() !== shown) set(shown);
	};

	st.raw = normalize(get());

	Object.defineProperty(input, 'value', {
		configurable: true,
		enumerable: desc.enumerable,
		get() {
			// 포커스 중에는 표시값 == 원값(사용자 편집 중) → native 를 그대로 읽는다
			return toValue(st.focused ? cleanText(get()) : st.raw);
		},
		set(v: unknown) {
			const nv = normalize(v);
			if (st.focused && st.numberMode) {
				// 편집 중 표시('1.', '1.0', '007' 등)와 수치가 같은 값이 다시 들어오면 표시를 유지한다.
				// (type=number 일 때 Vue v-model 은 수치 비교로 재설정을 건너뛰었다 — 그 동작 재현)
				const curText = stripComma(get());
				const cur = readNumberValue(curText);
				if (cur !== '' && nv !== '' && Number(cur) === Number(nv)) {
					st.raw = curText;
					return;
				}
			}
			st.raw = nv;
			render();
		},
	});

	const on = (type: string, fn: EventListener, capture = false) => {
		input.addEventListener(type, fn, capture);
		st.listeners.push([type, fn, capture]);
	};

	// 정제(입력 제한) — capture 단계: Vue/Vuetify 의 input 리스너보다 먼저 실행
	const clean = () => {
		const cur = get();
		const pos = input.selectionStart;
		const r = sanitizeNumberText(cur, st.allowNegative, pos, st.allowDot);
		if (r.value !== cur) {
			set(r.value);
			try { input.setSelectionRange(r.caret, r.caret); } catch { /* ignore */ }
		}
		st.raw = r.value;
	};

	on('focus', () => {
		const shown = get();
		// 클릭한 caret 위치를 콤마 제거 후 위치로 보정(앞쪽 콤마 개수만큼 당김)
		let pos: number | null = null;
		try { pos = input.selectionStart; } catch { /* ignore */ }
		st.focused = true;
		st.raw = stripComma(shown);
		if (shown !== st.raw) {
			set(st.raw);
			const p = pos === null ? st.raw.length : pos - (shown.slice(0, pos).match(/,/g)?.length ?? 0);
			try { input.setSelectionRange(p, p); } catch { /* ignore */ }
		}
	}, true);

	on('blur', () => {
		// compositionend 가 blur 보다 늦게 오는 키보드 대비 — blur 시점에도 정제한 값으로 확정
		if (st.focused) st.raw = cleanText(get());
		st.focused = false;
		render();
	}, true);

	on('input', (e: Event) => {
		if ((e as InputEvent).isComposing) return; // IME 조합 중에는 손대지 않음
		if (!st.focused) return; // 비포커스 상태의 합성 이벤트는 표시(콤마) 유지 — getter 가 원값을 준다
		clean();
	}, true);

	on('compositionend', () => {
		if (!st.focused) return;
		const before = get();
		clean();
		// 조합 결과에서 문자가 제거됐다면 모델 동기화를 위해 input 재통지(사용자 입력의 연장)
		if (get() !== before) input.dispatchEvent(new Event('input', { bubbles: true }));
	});

	// type=number 의 ↑/↓ 증감 재현 (v-number-input 은 Vuetify 가 자체 처리 → 제외)
	if (numberMode && !input.closest('.v-number-input')) {
		on('keydown', (e: Event) => {
			const ke = e as KeyboardEvent;
			if (ke.key !== 'ArrowUp' && ke.key !== 'ArrowDown') return;
			if (ke.altKey || ke.ctrlKey || ke.metaKey || ke.isComposing) return;
			if (input.readOnly || input.disabled) return;
			ke.preventDefault();
			let step = parseFloat(input.getAttribute('step') ?? '');
			if (!(step > 0)) step = 1;
			const curNum = parseFloat(input.value);
			const base = isNaN(curNum) ? 0 : curNum;
			let next = ke.key === 'ArrowUp' ? base + step : base - step;
			next = +next.toFixed(Math.max(decimalsOf(base), decimalsOf(step)));
			const max = parseFloat(input.getAttribute('max') ?? '');
			const min = parseFloat(input.getAttribute('min') ?? '');
			if (!isNaN(max) && next > max) next = max;
			if (!isNaN(min) && next < min) next = min;
			if (!st.allowNegative && next < 0) next = 0;
			if (String(next) === input.value) return;
			input.value = String(next);
			input.dispatchEvent(new Event('input', { bubbles: true }));
			input.dispatchEvent(new Event('change', { bubbles: true }));
		});
	}

	(input as any)[STATE_KEY] = st;
	(el as any)[STATE_KEY] = st;
	render();
}

function unbind(el: HTMLElement) {
	const st: CommaState = (el as any)?.[STATE_KEY];
	if (!st) return;
	const input = st.input;
	for (const [type, fn, capture] of st.listeners) input.removeEventListener(type, fn, capture);
	st.listeners = [];
	// prototype 복귀 전에 표시값을 원값으로 되돌려 둔다(복귀 후 value 가 콤마 문자열이 되지 않도록)
	if (st.get() !== st.raw) st.set(st.raw);
	delete (input as any).value;
	delete (input as any)[STATE_KEY];
	delete (el as any)[STATE_KEY];
}

export const vComma = {
	mounted(el: HTMLElement, binding: any) {
		bind(el, binding);
	},
	updated(el: HTMLElement, binding: any) {
		// 컴포넌트 재렌더로 내부 input 이 교체된 경우 재바인딩
		const st: CommaState = (el as any)?.[STATE_KEY];
		const input = findInput(el);
		if (st && st.input === input) return;
		if (st) unbind(el);
		bind(el, binding);
	},
	unmounted(el: HTMLElement) {
		unbind(el);
	},
};

export default vComma;
